import { useState } from "react";
import { Button } from "@/components/ui/Button";
import type { PoleParams, TechCalcDictionaries } from "../types";
import { constructionGroups, poleTypeOptions } from "../utils";

const NO_CHANGE = "";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] text-[var(--color-text-secondary)]">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

/**
 * Zbiorcza edycja parametrów obliczeniowych słupów zaznaczonych poligonem.
 * Każde pole domyślnie „— bez zmian —" (puste = nie ruszaj); „Zastosuj" zmienia
 * wyłącznie wypełnione pola u wszystkich zaznaczonych słupów.
 */
export function BulkParamsPanel({
  count,
  dictionaries,
  onApply,
  onClose,
}: {
  count: number;
  dictionaries: TechCalcDictionaries | undefined;
  /** Patch of the FILLED fields only — applied to every selected pole. */
  onApply: (patch: Partial<PoleParams>) => void;
  onClose: () => void;
}) {
  const [poleType, setPoleType] = useState(NO_CHANGE);
  const [construction, setConstruction] = useState(NO_CHANGE);
  const [material, setMaterial] = useState(NO_CHANGE);
  const [height, setHeight] = useState("");
  const [func, setFunc] = useState(NO_CHANGE);
  // Trójstanowo: "" = bez zmian, "in" = w opracowaniu, "out" = poza opracowaniem.
  const [scope, setScope] = useState<"" | "in" | "out">("");
  // Trójstanowo: "" = bez zmian, "yes" = oświetleniowy, "no" = zwykły.
  const [lighting, setLighting] = useState<"" | "yes" | "no">("");

  const poleTypes = poleTypeOptions(dictionaries);
  const groups = constructionGroups(dictionaries);
  const materials = dictionaries?.materials ?? [];
  const functions = dictionaries?.functions ?? [];

  const patch: Partial<PoleParams> = {
    ...(poleType ? { pole_type: poleType } : {}),
    ...(construction ? { construction } : {}),
    ...(material ? { material } : {}),
    ...(height.trim() ? { height: height.trim() } : {}),
    ...(func ? { function: func } : {}),
    ...(scope ? { out_of_scope: scope === "out" } : {}),
    ...(lighting ? { is_lighting: lighting === "yes" } : {}),
  };
  const hasAny = Object.keys(patch).length > 0;

  return (
    <div className="p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
          Zbiorcza edycja — {count} słupów
        </span>
        <button
          type="button"
          className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
          onClick={onClose}
        >
          Zamknij
        </button>
      </div>

      <div className="space-y-2">
        <Field label="Typ słupa">
          <select
            className="input-base py-1 text-[12px]"
            value={poleType}
            onChange={(e) => setPoleType(e.target.value)}
          >
            <option value={NO_CHANGE}>— bez zmian —</option>
            {poleTypes.map((t) => (
              <option key={t.value} value={t.value} disabled={!t.resolvable}>
                {t.value}
                {!t.resolvable ? " — brak w katalogu norm" : ""}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Konstrukcja">
          <select
            className="input-base py-1 text-[12px]"
            value={construction}
            onChange={(e) => setConstruction(e.target.value)}
          >
            <option value={NO_CHANGE}>— bez zmian —</option>
            {groups.map((g) =>
              g.group ? (
                <optgroup key={g.group} label={g.group}>
                  {g.values.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </optgroup>
              ) : (
                g.values.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))
              ),
            )}
          </select>
        </Field>

        <Field label="Materiał">
          <select
            className="input-base py-1 text-[12px]"
            value={material}
            onChange={(e) => setMaterial(e.target.value)}
          >
            <option value={NO_CHANGE}>— bez zmian —</option>
            {materials.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Wysokość [m]">
          <input
            className="input-base py-1 font-mono text-[12px]"
            placeholder="— bez zmian —"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
          />
        </Field>

        <Field label="Funkcja">
          <select
            className="input-base py-1 text-[12px]"
            value={func}
            onChange={(e) => setFunc(e.target.value)}
          >
            <option value={NO_CHANGE}>— bez zmian —</option>
            {functions.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Zakres opracowania">
          <select
            className="input-base py-1 text-[12px]"
            value={scope}
            onChange={(e) => setScope(e.target.value as "" | "in" | "out")}
          >
            <option value="">— bez zmian —</option>
            <option value="in">w opracowaniu (liczony)</option>
            <option value="out">poza opracowaniem (bez obliczeń)</option>
          </select>
        </Field>

        <Field label="Oświetleniowy">
          <select
            className="input-base py-1 text-[12px]"
            value={lighting}
            onChange={(e) => setLighting(e.target.value as "" | "yes" | "no")}
          >
            <option value="">— bez zmian —</option>
            <option value="yes">tak (Fl = 22 daN)</option>
            <option value="no">nie (Fl = 0)</option>
          </select>
        </Field>

        <Button size="sm" className="w-full" disabled={!hasAny} onClick={() => onApply(patch)}>
          Zastosuj do {count} słupów
        </Button>
        <p className="text-[10px] text-[var(--color-text-tertiary)]">
          Puste pola nie zmieniają nic u zaznaczonych słupów.
        </p>
      </div>
    </div>
  );
}
